/* eslint-disable no-param-reassign */
/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// react-router components
import { Link } from "react-router-dom";

// @mui material components
import Icon from "@mui/material/Icon";
import Container from "@mui/material/Container";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI examples components
import DefaultNavbarLink from "examples/Navbars/DefaultNavbar/DefaultNavbarLink";
import DefaultNavbarMobile from "examples/Navbars/DefaultNavbar/DefaultNavbarMobile";

// Argon Dashboard 2 MUI Base Styles
import breakpoints from "assets/theme/base/breakpoints";

// Material Dashboard 2 PRO React context
import { useArgonController } from "context";
import { Menu, MenuItem } from "@mui/material";
import DefaultNavbarDropdown from "./DefaultNavbarDropdown";

import logo_buton from "assets/images/logo-buton.png"

function DefaultNavbar({ brand, transparent, light, routes }) {
    const [controller] = useArgonController();
    const { darkMode } = controller;
    const [mobileNavbar, setMobileNavbar] = useState(false);
    const [mobileView, setMobileView] = useState(false);

    const openMobileNavbar = ({ currentTarget }) => {
        mobileNavbar ? setMobileNavbar(false) : setMobileNavbar(currentTarget.parentNode)
    };
    // const closeMobileNavbar = () => setMobileNavbar(false);

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const [openMenu, setOpenMenu] = useState('')

    const handlePopoverOpen = (event, name) => {
        setAnchorEl(event.currentTarget);
        setOpenMenu(name)
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    useEffect(() => {
        // A function that sets the display state for the DefaultNavbarMobile.
        function displayMobileNavbar() {
            if (window.innerWidth < breakpoints.values.lg) {
                setMobileView(true);
                setMobileNavbar(false);
            } else {
                setMobileView(false);
                setMobileNavbar(false);
            }
        }

        window.addEventListener("resize", displayMobileNavbar);

        // Call the displayMobileNavbar function to set the state with the initial value.
        displayMobileNavbar();

        // Remove event listener on cleanup
        return () => window.removeEventListener("resize", displayMobileNavbar);
    }, []);

    const renderMenu = (collapse, key, name) => {
        const subMenu = collapse.map(({ name, key, route }) => {
            return (<MenuItem key={key} component={Link} to={route} onClick={handlePopoverClose}>{name}</MenuItem>)
        })

        return (
            <Menu
                id={key}
                anchorEl={anchorEl}
                name={name}
                open={open && openMenu === name}
                onClose={handlePopoverClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
            >
                {subMenu}
            </Menu>
        );
    }

    const renderRoutes = routes.map(({ type, name, icon, route, collapse, key }) => {
        let returnValue;

        if (type === "route") {
            returnValue = (
                <DefaultNavbarLink
                    key={key}
                    icon={icon}
                    name={name}
                    route={route}
                    light={light}
                />
            );
        }
        else if (type === 'collapse') {
            returnValue = (
                <DefaultNavbarDropdown
                    key={key}
                    icon={icon}
                    name={name}
                    light={light}
                    collapseStatus={open}
                    collapse
                    onClick={(event) => handlePopoverOpen(event, name)}
                // children={collapse && renderMenu(collapse, key)}
                >
                    {renderMenu(collapse, key, name)}
                </DefaultNavbarDropdown>
            )
        }

        return returnValue;
    });

    return (
        <Container>
            <ArgonBox
                pt={0.75}
                pb={1}
                px={{ xs: 4, sm: transparent ? 2 : 3, lg: transparent ? 0 : 2 }}
                my={2}
                mx={3}
                width="calc(100% - 48px)"
                borderRadius="lg"
                shadow={transparent ? "none" : "md"}
                color={light ? "white" : "dark"}
                position="absolute"
                left={0}
                zIndex={99}
                sx={({
                    palette: { transparent: transparentColor, white, background },
                    functions: { rgba },
                }) => ({
                    backgroundColor: transparent
                        ? transparentColor.main
                        : rgba(darkMode ? background.dark : white.main, 0.8),
                    backdropFilter: transparent ? "none" : `saturate(200%) blur(30px)`,
                })}
            >
                <ArgonBox display="flex" justifyContent="space-between" alignItems="center" px={2}>
                    <ArgonBox component={Link} to="/" py={transparent ? 1.5 : 0.75} lineHeight={1}>
                        <ArgonBox
                            component="img"
                            src={logo_buton}
                            alt="background"
                            width="15px"
                            sx={{
                                verticalAlign: "middle",
                            }}
                            mr={1}
                        />
                        <ArgonTypography variant="button" fontWeight="bold" color={light ? "white" : "dark"}>
                            {brand}
                        </ArgonTypography>
                    </ArgonBox>
                    <ArgonBox color="inherit" display={{ xs: "none", lg: "flex" }} m={0} p={0}>
                        {renderRoutes}
                    </ArgonBox>
                    <ArgonBox
                        display={{ xs: "inline-block", lg: "none" }}
                        lineHeight={0}
                        py={1.5}
                        pl={1.5}
                        color="inherit"
                        sx={{ cursor: "pointer" }}
                        onClick={openMobileNavbar}
                    >
                        <Icon fontSize="default">{mobileNavbar ? "close" : "menu"}</Icon>
                    </ArgonBox>
                </ArgonBox>
                {mobileView && <DefaultNavbarMobile open={mobileNavbar} routes={routes} setOpen={setMobileNavbar} />}
            </ArgonBox>
        </Container>
    );
}

// Declaring default props for DefaultNavbar
DefaultNavbar.defaultProps = {
    brand: "TEPRA Kab. Buton",
    transparent: false,
    light: false,
};

// Typechecking props for the DefaultNavbar
DefaultNavbar.propTypes = {
    brand: PropTypes.string,
    transparent: PropTypes.bool,
    light: PropTypes.bool,
    action: PropTypes.shape({
        type: PropTypes.oneOf(["external", "internal"]).isRequired,
        route: PropTypes.string.isRequired,
        variant: PropTypes.oneOf(["contained", "outlined", "gradient"]),
        color: PropTypes.oneOf([
            "primary",
            "secondary",
            "info",
            "success",
            "warning",
            "error",
            "dark",
            "light",
            "default",
            "white",
        ]),
        label: PropTypes.string.isRequired,
    }),
};

export default DefaultNavbar;
